import { ComponentType } from 'react'

// This file will be populated as we migrate pages
// For now, we'll create placeholder components and add real ones as we migrate

/**
 * Route Registry
 * Maps database route paths to React components
 * This must include ALL possible routes that can come from the database
 */
// Import migrated pages
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import TwoFactor from '@/pages/TwoFactor'
import TwoFactorSetup from '@/pages/TwoFactor/Setup'
import Profile from '@/pages/Profile'
import UsersPage from '@/pages/UserManagement/Users'
import CreateUserPage from '@/pages/UserManagement/Users/Create'
import EditUserPage from '@/pages/UserManagement/Users/Edit'
import RolesPage from '@/pages/UserManagement/Roles'
import CreateRolePage from '@/pages/UserManagement/Roles/Create'
import EditRolePage from '@/pages/UserManagement/Roles/Edit'
import UserActivityPage from '@/pages/UserManagement/UserActivity'
import GeneralSettingsPage from '@/pages/SystemSettings/General'
import EmailSettingsPage from '@/pages/SystemSettings/EmailSettings'
import SecuritySettingsPage from '@/pages/SystemSettings/Security'
import NavigationSettingsPage from '@/pages/SystemSettings/Navigation'
import CreateNavigationPage from '@/pages/SystemSettings/Navigation/Create'
import EditNavigationPage from '@/pages/SystemSettings/Navigation/Edit'
import ClientsPage from '@/pages/Clients'
import CreateClientPage from '@/pages/Clients/Create'
import EditClientPage from '@/pages/Clients/Edit'
import FieldEngineersPage from '@/pages/FieldEngineers'
import CreateFieldEngineerPage from '@/pages/FieldEngineers/Create'
import EditFieldEngineerPage from '@/pages/FieldEngineers/Edit'
import ClustersPage from '@/pages/Clusters'
import CreateClusterPage from '@/pages/Clusters/Create'
import EditClusterPage from '@/pages/Clusters/Edit'
import DepartmentsPage from '@/pages/Departments'
import CreateDepartmentPage from '@/pages/Departments/Create'
import EditDepartmentPage from '@/pages/Departments/Edit'
import TeamsPage from '@/pages/Organization/Teams'
import CreateTeamPage from '@/pages/Organization/Teams/Create'
import EditTeamPage from '@/pages/Organization/Teams/Edit'
import AssignedDevicesPage from '@/pages/UserManagement/AssignedDevices'
import CreateAssignedDevicePage from '@/pages/UserManagement/AssignedDevices/Create'
import EditAssignedDevicePage from '@/pages/UserManagement/AssignedDevices/Edit'
import BackupRestorePage from '@/pages/SystemSettings/BackupRestore'
import CreateBackupPage from '@/pages/SystemSettings/BackupRestore/Create'
import BackupSchedulesPage from '@/pages/SystemSettings/BackupRestore/Schedules'
import CreateSchedulePage from '@/pages/SystemSettings/BackupRestore/Schedules/Create'
import EditSchedulePage from '@/pages/SystemSettings/BackupRestore/Schedules/Edit'
import EmailTemplatePage from '@/pages/SystemSettings/EmailTemplate'
import CreateEmailTemplatePage from '@/pages/SystemSettings/EmailTemplate/Create'
import EditEmailTemplatePage from '@/pages/SystemSettings/EmailTemplate/Edit'
import ApplicationsPage from '@/pages/SystemSettings/Applications'
import CreateApplicationPage from '@/pages/SystemSettings/Applications/Create'
import EditApplicationPage from '@/pages/SystemSettings/Applications/Edit'

export const routeRegistry: Record<string, ComponentType<any>> = {
  // Public routes
  '/login': Login,
  '/forgot-password': ForgotPassword,
  '/reset-password': ResetPassword,
  '/two-factor': TwoFactor,
  '/two-factor/setup': TwoFactorSetup,

  // Dashboard (shared)
  '/dashboard': Dashboard,

  // Profile (shared)
  '/profile': Profile,

  // ============================================
  // AUTH APPLICATION (auth.actionlabs.com.ph)
  // ============================================

  // Organization Section
  '/organization/clusters': ClustersPage,
  '/organization/clusters/create': CreateClusterPage,
  '/organization/clusters/:id': EditClusterPage,
  '/organization/departments': DepartmentsPage,
  '/organization/departments/create': CreateDepartmentPage,
  '/organization/departments/:id': EditDepartmentPage,
  '/organization/teams': TeamsPage,
  '/organization/teams/create': CreateTeamPage,
  '/organization/teams/:id': EditTeamPage,
  '/organization/clients': ClientsPage,
  '/organization/clients/create': CreateClientPage,
  '/organization/clients/:id': EditClientPage,
  '/organization/field-engineers': FieldEngineersPage,
  '/organization/field-engineers/create': CreateFieldEngineerPage,
  '/organization/field-engineers/:id': EditFieldEngineerPage,

  // Access Control Section
  '/access-control/users': UsersPage,
  '/access-control/users/create': CreateUserPage,
  '/access-control/users/:id': EditUserPage,
  '/access-control/roles': RolesPage,
  '/access-control/roles/create': CreateRolePage,
  '/access-control/roles/:id': EditRolePage,
  '/access-control/user-activity/:id': UserActivityPage,
  '/access-control/assigned-devices': AssignedDevicesPage,
  '/access-control/assigned-devices/create': CreateAssignedDevicePage,
  '/access-control/assigned-devices/:id': EditAssignedDevicePage,

  // System Settings Section
  '/system-settings/general': GeneralSettingsPage,
  '/system-settings/settings': GeneralSettingsPage, // Alias for backward compatibility
  '/system-settings/security': SecuritySettingsPage,
  '/system-settings/email-settings': EmailSettingsPage,
  '/system-settings/email-template': EmailTemplatePage,
  '/system-settings/email-template/create': CreateEmailTemplatePage,
  '/system-settings/email-template/:id': EditEmailTemplatePage,
  '/system-settings/applications': ApplicationsPage,
  '/system-settings/applications/create': CreateApplicationPage,
  '/system-settings/applications/:id': EditApplicationPage,
  '/system-settings/navigation': NavigationSettingsPage,
  '/system-settings/navigation/create': CreateNavigationPage,
  '/system-settings/navigation/:id': EditNavigationPage,
  '/system-settings/backup-restore': BackupRestorePage,
  '/system-settings/backup-restore/create': CreateBackupPage,
  '/system-settings/backup-restore/schedules': BackupSchedulesPage,
  '/system-settings/backup-restore/schedules/create': CreateSchedulePage,
  '/system-settings/backup-restore/schedules/:id': EditSchedulePage,

  // ============================================
  // SIMA APPLICATION (workhub-sima.actionlabs.com.ph)
  // ============================================
  // TODO: Add routes when pages are created for SIMA application:
  // Master Data: '/master-data/media-library', '/master-data/categories', '/master-data/brands', etc.
  // Store Management: '/store-management/stores', '/store-management/business-unit'
  // Inventory Management: '/inventory-management/assets', '/inventory-management/asset-assignments'
  // Audit Management: '/audit-management/audit-requests', '/audit-management/reports'
  // Communication: '/communication/announcements'

  // ============================================
  // ADMIN-SUPPORT APPLICATION (admin-support.actionlabs.com.ph)
  // ============================================
  // TODO: Add routes when pages are created:
  // '/ticketing/admin/tickets': AdminTicketsPage,
  // '/ticketing/admin/service-tasks': ServiceTasksPage,

  // ============================================
  // SUPPORT APPLICATION (support.actionlabs.com.ph)
  // ============================================
  // TODO: Add routes when pages are created:
  // '/ticketing/client/tickets': MyRequestsPage,
  // '/ticketing/client/tickets/create': SubmitRequestPage,
  // '/ticketing/client/tickets/status': RequestStatusPage,
  // '/ticketing/client/tickets/history': RequestHistoryPage,
  // '/reports': ReportsPage,
  // '/reports/audit-reports': AuditReportsPage,
  // '/help-center': HelpCenterPage,
}

/**
 * Get component for a route path
 * Handles dynamic segments (e.g., :id, :userId)
 */
export function getRouteComponent(path: string): ComponentType<any> | null {
  // Direct match
  if (routeRegistry[path]) {
    return routeRegistry[path]
  }

  // Try to match dynamic routes
  // e.g., "/categories/123" matches "/categories/:id"
  const dynamicPatterns = Object.keys(routeRegistry).filter(key => key.includes(':'))

  for (const pattern of dynamicPatterns) {
    const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$')
    if (regex.test(path)) {
      return routeRegistry[pattern]
    }
  }

  return null
}

